import React from 'react';
import { Link } from 'react-router-dom';
import { Book, Users, HelpCircle, Home, BookOpen, Mail, BookPlus, Target, Youtube, Instagram, UserCircle } from 'lucide-react';

const getSignedInPlayer = () => {
  const raw = localStorage.getItem('playerAuth');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const Layout = ({ children, isAdmin = false }) => {
  const player = getSignedInPlayer();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                <Book className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  BookQuiz
                </h1>
                <p className="text-xs text-gray-600">Interactive Book Testing</p>
              </div>
            </Link>
            
            {isAdmin && (
              <nav className="hidden md:flex items-center space-x-6">
                <Link
                  to="/admin"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/admin/books"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Books</span>
                </Link>
                <Link
                  to="/admin/quizzes"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>Quizzes</span>
                </Link>
                <Link
                  to="/admin/suggestions"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <BookPlus className="h-4 w-4" />
                  <span>Suggestions</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <Users className="h-4 w-4" />
                  <span>Users</span>
                </Link>
              </nav>
            )}
            
            {!isAdmin && (
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-gray-700 hover:text-blue-600 transition-colors"
                >
                  Browse Books
                </Link>
                {player ? (
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    <UserCircle className="h-4 w-4" />
                    <span>{player.name || player.email}</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-white/20 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <Book className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  BookQuiz
                </h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Discover the joy of reading through interactive quizzes. Test your knowledge, 
                explore new books, and deepen your understanding of literature.
              </p>
              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="h-4 w-4" />
                <a href="mailto:j@jeet.world" className="text-blue-600 hover:underline">
                  j@jeet.world
                </a>
              </div>
              <div className="flex items-center space-x-2 text-gray-600 mt-2">
                <Youtube className="h-4 w-4" />
                <a
                  href="https://www.youtube.com/@bookquizzes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  YouTube Channel
                </a>
              </div>
              <div className="flex items-center space-x-2 text-gray-600 mt-2">
                <Instagram className="h-4 w-4" />
                <a
                  href="https://instagram.com/book.quiz.master"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Instagram
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">For Readers</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/" className="hover:text-blue-600 flex items-center"><BookOpen className="h-3 w-3 mr-1" />Browse Books</Link></li>
                <li><Link to="/suggest-book" className="hover:text-blue-600 flex items-center">
                  <BookPlus className="h-3 w-3 mr-1" />
                  Suggest a Book
                </Link></li>
                <li><Link to="/request-quiz" className="hover:text-blue-600 flex items-center">
                  <Target className="h-3 w-3 mr-1" />
                  Request a Quiz
                </Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="mailto:j@jeet.world" className="hover:text-blue-600">Contact Us</a></li>
                {!isAdmin && (
                  <li><Link to="/admin/login" className="hover:text-blue-600">Admin</Link></li>
                )}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-6 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-600">
              &copy; 2026 BookQuiz. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <a href="mailto:j@jeet.world" className="text-gray-600 hover:text-blue-600">
                <Mail className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@bookquizzes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600"
                aria-label="YouTube Channel"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="https://instagram.com/book.quiz.master"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
